<!-- FA Tasks Table Card -->
<div class="bai-surface-card">

  <!-- Card header -->
  <div class="bai-section-header">
    <span class="bai-section-title">FA Tasks</span>
    <p-button
      icon="pi pi-plus"
      [text]="true"
      size="small"
      (onClick)="openAddTask(opportunities())"
      aria-label="Add FA task"
    />
  </div>

  <!-- Filter bar — all dropdowns to keep the bar compact -->
  <div class="flex flex-wrap gap-2 px-4 py-2 border-b border-gray-100" role="group" aria-label="Filter FA tasks">

    <p-select
      [options]="statusOptions"
      [ngModel]="taskFilterValue()"
      (ngModelChange)="taskFilterValue.set($event)"
      optionLabel="label"
      optionValue="value"
      [styleClass]="taskFilterValue() ? 'bai-fat-select bai-fat-select--active' : 'bai-fat-select'"
      aria-label="Filter by status"
    />

    <p-select
      [options]="priorityOptions"
      [ngModel]="priorityFilterValue()"
      (ngModelChange)="priorityFilterValue.set($event)"
      optionLabel="label"
      optionValue="value"
      [styleClass]="priorityFilterValue() ? 'bai-fat-select bai-fat-select--active' : 'bai-fat-select'"
      aria-label="Filter by priority"
    />

    @if (allLabels().length > 0) {
      <p-select
        [options]="labelOptions()"
        [ngModel]="labelFilterValue()"
        (ngModelChange)="labelFilterValue.set($event)"
        optionLabel="label"
        optionValue="value"
        [styleClass]="labelFilterValue() ? 'bai-fat-select bai-fat-select--active' : 'bai-fat-select'"
        aria-label="Filter by label"
      />
    }

    <p-select
      [options]="dueDateOptions"
      [ngModel]="dueDateFilter()"
      (ngModelChange)="dueDateFilter.set($event)"
      optionLabel="label"
      optionValue="value"
      [styleClass]="dueDateFilter() !== 'all' ? 'bai-fat-select bai-fat-select--active' : 'bai-fat-select'"
      aria-label="Filter by due date window"
    />

    @if (allAssignees().length > 1) {
      <p-select
        [options]="assigneeOptions()"
        [ngModel]="assigneeFilter()"
        (ngModelChange)="assigneeFilter.set($event)"
        optionLabel="label"
        optionValue="value"
        [styleClass]="assigneeFilter() ? 'bai-fat-select bai-fat-select--active' : 'bai-fat-select'"
        aria-label="Filter by assignee"
      />
    }

    <p-select
      [options]="sortOptions"
      [ngModel]="sortBy()"
      (ngModelChange)="sortBy.set($event)"
      optionLabel="label"
      optionValue="value"
      styleClass="bai-fat-select"
      aria-label="Sort FA tasks by"
    />

  </div>

  <!-- Table -->
  <div class="overflow-x-auto">
    @if (filteredTasks().length === 0) {
      <p class="text-sm text-gray-400 px-4 py-4">No FA tasks match the selected filters.</p>
    } @else {
      <table class="bai-actions-table bai-fat-table text-sm" aria-label="FA Tasks">
        <thead>
          <tr>
            <th baiColResize scope="col">Task Name</th>
            <th baiColResize scope="col">Description</th>
            <th baiColResize scope="col">Opportunity</th>
            <th baiColResize scope="col">Assignee</th>
            <th baiColResize scope="col" class="bai-cell-nowrap">Priority</th>
            <th baiColResize scope="col" class="bai-cell-nowrap">Status</th>
            <th baiColResize scope="col">Labels</th>
            <th baiColResize scope="col" class="bai-cell-nowrap">Subtasks</th>
            <th baiColResize scope="col" class="bai-cell-nowrap">Created</th>
            <th baiColResize scope="col" class="bai-cell-nowrap">Due Date</th>
            <th baiColResize scope="col" class="bai-cell-nowrap">Planned</th>
            <th baiColResize scope="col" class="bai-cell-nowrap">Updated</th>
            <th scope="col" class="w-24 bai-cell-nowrap">More</th>
          </tr>
        </thead>
        <tbody>
          @for (task of filteredTasks(); track task.id) {
            <tr
              class="cursor-pointer hover:bg-gray-50"
              (click)="openTaskDetails(task, opportunities())"
            >
              <td [title]="task.taskName">
                {{ task.taskName }}
              </td>
              <td [title]="task.description">
                {{ task.description || '—' }}
              </td>
              <td [title]="opportunityLabel(task.opportunityId, opportunities())">
                {{ opportunityLabel(task.opportunityId, opportunities()) }}
              </td>
              <td>{{ task.assignee }}</td>
              <td class="bai-cell-nowrap">
                <p-tag
                  [value]="task.priority"
                  [severity]="prioritySeverity(task.priority)"
                  styleClass="text-xs"
                />
              </td>
              <td class="bai-cell-nowrap">
                <p-tag
                  [value]="task.status"
                  [severity]="statusSeverity(task.status)"
                  styleClass="text-xs"
                />
              </td>
              <td>
                @if (task.labels.length > 0) {
                  <div class="flex flex-wrap gap-1">
                    @for (label of task.labels.slice(0, 2); track label) {
                      <span class="fat-label-chip">{{ label }}</span>
                    }
                    @if (task.labels.length > 2) {
                      <span class="fat-label-chip fat-label-chip--more">+{{ task.labels.length - 2 }}</span>
                    }
                  </div>
                } @else {
                  <span class="text-gray-400">—</span>
                }
              </td>
              <td class="bai-cell-nowrap">
                @if (subtaskProgress(task)) {
                  <span class="fat-subtask-progress">{{ subtaskProgress(task) }}</span>
                } @else {
                  <span class="text-gray-400">—</span>
                }
              </td>
              <td class="bai-cell-nowrap">{{ task.createdDate }}</td>
              <td class="bai-cell-nowrap" [class.fat-overdue]="isOverdue(task)">
                {{ task.dueDate || '—' }}
              </td>
              <td class="bai-cell-nowrap">{{ task.plannedDate || '—' }}</td>
              <td class="bai-cell-nowrap">{{ task.updatedDate }}</td>
              <td (click)="$event.stopPropagation(); $any($event.target).closest('[class*=__action]') && $any($event.currentTarget).removeAttribute('open')">
                <details class="bai-copilot-kebab" (click)="$event.stopPropagation(); $any($event.target).closest('[class*=__action]') && $any($event.currentTarget).removeAttribute('open')">
                  <summary class="bai-copilot-kebab__btn" aria-label="FA task options">
                    <i class="pi pi-bars" aria-hidden="true"></i>
                  </summary>
                  <button
                    type="button"
                    class="bai-copilot-kebab__action"
                    (click)="addTaskToCopilot(task)"
                  >
                    Add to Copilot
                  </button>
                </details>
              </td>
            </tr>
          }
        </tbody>
      </table>
    }
  </div>

</div>
